import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import QRCode from 'qrcode';
import type { CardTemplate, CardElement } from './scoreCardTypes';
import { resolveBindings, buildLiveScoringUrl, DESIGNER_CANVAS_W } from './scoreCardTypes';

// ─── Business card aspect ratio constants ─────────────────────────────────────
// Canonical card: 3.5" wide × 2.5" tall  →  7:5 ratio
export const CARD_ASPECT_W = 7;
export const CARD_ASPECT_H = 5;

interface ScoreCodeCardItemProps {
    template: CardTemplate;
    registration: Record<string, unknown>;
    roundValue: number;
    /** Event document for binding fallbacks (e.g. round startHole). */
    eventData?: Record<string, unknown>;
    /** Display width in px — height is derived from the 7:5 aspect ratio. */
    displayWidth?: number;
    /** When true, renders a thin border around the card (used in grid view). */
    showBorder?: boolean;
    /** Optional ref forwarded to the outer card div (used for screenshot/export). */
    cardRef?: React.RefObject<HTMLDivElement>;
}

// ─── QR image rendered from a data URL so it works inside a positioned canvas ─

function QrImage({ url, width, height }: { url: string; width: number; height: number }) {
    const [dataUrl, setDataUrl] = useState<string>('');

    useEffect(() => {
        if (!url) return;
        QRCode.toDataURL(url, { margin: 1, width: Math.round(Math.max(width, height) * 2) })
            .then(setDataUrl)
            .catch(() => setDataUrl(''));
    }, [url, width, height]);

    if (!dataUrl) return null;
    return (
        <img
            src={dataUrl}
            alt="QR Code"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
    );
}

// ─── Single card element renderer ─────────────────────────────────────────────

function CardElementRenderer({
    element,
    registration,
    roundValue,
    eventData,
    cardWidth,
    cardHeight,
}: {
    element: CardElement;
    registration: Record<string, unknown>;
    roundValue: number;
    eventData?: Record<string, unknown>;
    cardWidth: number;
    cardHeight: number;
}) {
    const left   = (element.x      / 100) * cardWidth;
    const top    = (element.y      / 100) * cardHeight;
    const width  = (element.width  / 100) * cardWidth;
    const height = (element.height / 100) * cardHeight;

    // Scale font sizes proportionally so text renders correctly at any display size.
    const scaleFactor = cardWidth / DESIGNER_CANVAS_W;

    const posStyle: React.CSSProperties = {
        position: 'absolute',
        left,
        top,
        width,
        height,
        overflow: 'hidden',
        boxSizing: 'border-box',
    };

    if (element.type === 'text') {
        const resolved = resolveBindings(element.content ?? '', registration, roundValue, eventData);
        return (
            <div style={posStyle}>
                <Typography
                    component="span"
                    sx={{
                        fontFamily: 'Fredoka, sans-serif',
                        fontSize: (element.fontSize ?? 12) * scaleFactor,
                        fontWeight: element.fontWeight ?? 'normal',
                        color: element.color ?? '#000000',
                        textAlign: element.textAlign ?? 'left',
                        lineHeight: 1.3,
                        display: 'block',
                        width: '100%',
                        wordBreak: 'break-word',
                    }}
                >
                    {resolved}
                </Typography>
            </div>
        );
    }

    if (element.type === 'image') {
        if (!element.imageDataUrl) return null;
        return (
            <div style={posStyle}>
                <img
                    src={element.imageDataUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                />
            </div>
        );
    }

    if (element.type === 'qrcode') {
        const eventId   = String((registration as any).eventID ?? (registration as any).eventScoreID ?? '');
        const scoreCode = String((registration as any).scoreCode ?? '');
        const url = buildLiveScoringUrl(eventId, scoreCode);
        return (
            <div style={posStyle}>
                <QrImage url={url} width={width} height={height} />
            </div>
        );
    }

    return null;
}

// ─── ScoreCodeCardItem ────────────────────────────────────────────────────────

export const ScoreCodeCardItem = React.forwardRef<HTMLDivElement, ScoreCodeCardItemProps>(
    function ScoreCodeCardItem(
        { template, registration, roundValue, eventData, displayWidth = 280, showBorder = false, cardRef },
        ref,
    ) {
        const cardWidth  = displayWidth;
        const cardHeight = Math.round((displayWidth * CARD_ASPECT_H) / CARD_ASPECT_W);

        return (
            <Box
                ref={cardRef ?? ref}
                sx={{
                    position: 'relative',
                    width: cardWidth,
                    height: cardHeight,
                    backgroundColor: template.backgroundColor,
                    border: showBorder ? '1px solid rgba(0,0,0,0.18)' : 'none',
                    borderRadius: 1,
                    overflow: 'hidden',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                }}
            >
                {template.elements.map((el) => (
                    <CardElementRenderer
                        key={el.id}
                        element={el}
                        registration={registration}
                        roundValue={roundValue}
                        eventData={eventData}
                        cardWidth={cardWidth}
                        cardHeight={cardHeight}
                    />
                ))}
            </Box>
        );
    },
);
